import { Link } from "react-router-dom";
import type { MediaItem } from "@/mocks/media";
import { typeLabels } from "@/mocks/filters";
import { formatDate } from "@/hooks/useMediaQuery";

type Props = {
  item: MediaItem;
  showType?: boolean;
  compact?: boolean;
};

export function FavoriteButton({ item }: { item: MediaItem }) {
  return (
    <button
      type="button"
      className="flex h-8 w-8 items-center justify-center rounded-full bg-background-50/90 text-foreground-700 transition-colors hover:text-accent-600"
      aria-label="收藏"
    >
      <i className="ri-heart-3-line text-sm" />
    </button>
  );
}

export default function MediaCard({ item, showType = false, compact = false }: Props) {
  return (
    <article className="group" data-product-shop>
      <Link to={`/detail/${item.id}`} className="block">
        <div
          className={`relative w-full overflow-hidden rounded-lg bg-background-200 ${
            compact ? "aspect-[3/4]" : "aspect-[2/3]"
          }`}
        >
          <img
            src={item.cover}
            alt={`${item.title} 封面`}
            title={`${item.title} ${typeLabels[item.type]} ${item.year}`}
            className="h-full w-full object-cover object-top transition-transform duration-500 ease-smooth group-hover:scale-[1.04]"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-primary-950/75 via-primary-950/5 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
          <div className="absolute left-2 top-2 flex items-center gap-1 rounded-sm bg-primary-950/70 px-1.5 py-0.5 font-label text-[10px] font-medium text-background-50 backdrop-blur-sm">
            <i className="ri-star-fill text-[10px] text-accent-400" />
            {item.rating.toFixed(1)}
          </div>
          <div className="absolute right-2 top-2 rounded-sm bg-accent-500 px-1.5 py-0.5 font-label text-[10px] font-semibold text-background-50">
            {item.status}
          </div>
          <div className="absolute inset-x-0 bottom-0 translate-y-2 p-2.5 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
            <span className="flex items-center justify-center gap-1 rounded-md bg-background-50/95 py-1.5 text-[11px] font-medium text-foreground-900">
              <i className="ri-play-circle-line flex h-4 w-4 items-center justify-center" />
              查看详情
            </span>
          </div>
        </div>
      </Link>
      <div className="mt-2.5">
        <Link
          to={`/detail/${item.id}`}
          className="line-clamp-1-custom block text-[13px] font-medium leading-5 text-foreground-900 transition-colors hover:text-accent-600 md:text-sm"
        >
          {item.title}
        </Link>
        <p className="mt-1 flex items-center gap-1.5 font-label text-[11px] text-foreground-500">
          {showType ? (
            <>
              <span className="rounded-sm bg-secondary-200/70 px-1 py-0.5 text-[10px] text-secondary-800">
                {typeLabels[item.type]}
              </span>
              <span>{item.year}</span>
            </>
          ) : (
            <>
              <span>{item.genres[0]}</span>
              <span className="h-2.5 w-px bg-background-300" />
              <span>{item.region}</span>
              <span className="h-2.5 w-px bg-background-300" />
              <span>{formatDate(item.updatedAt)}</span>
            </>
          )}
        </p>
      </div>
    </article>
  );
}

export function MediaListRow({ item }: { item: MediaItem }) {
  return (
    <article className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-accent-300 md:p-4">
      <Link to={`/detail/${item.id}`} className="block h-28 w-20 shrink-0 overflow-hidden rounded-md bg-background-200 md:h-32 md:w-24">
        <img
          src={item.cover}
          alt={`${item.title} 封面`}
          title={`${item.title} ${item.year}`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </Link>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-start justify-between gap-3">
          <Link
            to={`/detail/${item.id}`}
            className="line-clamp-1-custom font-heading text-sm font-semibold text-foreground-950 transition-colors group-hover:text-accent-600 md:text-base"
          >
            {item.title}
          </Link>
          <span className="flex shrink-0 items-center gap-1 font-label text-xs font-semibold text-accent-600">
            <i className="ri-star-fill text-[11px]" />
            {item.rating.toFixed(1)}
          </span>
        </div>
        <p className="mt-1.5 line-clamp-1-custom text-xs text-foreground-600 md:text-[13px]">
          主演：{item.cast.join(" / ")}
        </p>
        <p className="mt-1 hidden text-xs leading-relaxed text-foreground-500 sm:line-clamp-2">
          {item.synopsis}
        </p>
        <div className="mt-auto flex flex-wrap items-center gap-2 pt-2">
          {item.genres.slice(0, 2).map((g) => (
            <span key={g} className="rounded-sm bg-secondary-100 px-1.5 py-0.5 text-[10px] text-secondary-800">
              {g}
            </span>
          ))}
          <span className="font-label text-[11px] text-foreground-500">
            {item.region} · {item.year} · {item.episodes > 1 ? `${item.episodes}集` : item.duration}
          </span>
        </div>
      </div>
    </article>
  );
}